import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ArrowRightLeft,
  FileText,
  Inbox,
  Phone,
  Send,
  StickyNote,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { CrmActivity, CrmActivityType, CrmStage } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getLanguage, t } from '@/i18n';
import { fmtDate, relativeDay } from './deals/dealMath';

export type TimelineFilter = 'all' | 'entries' | 'stageChanges';

/** A body past either limit is clamped to four lines behind a "more" link. */
const LONG_BODY_CHARS = 280;
const LONG_BODY_LINES = 4;

const ICONS: Partial<Record<CrmActivityType, { icon: LucideIcon; tint: string }>> = {
  stage_change: { icon: ArrowRightLeft, tint: 'bg-primary/15 text-primary' },
  call: { icon: Phone, tint: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  meeting: { icon: Users, tint: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  email_out: { icon: Send, tint: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  email_in: { icon: Inbox, tint: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  note: { icon: StickyNote, tint: 'bg-muted text-muted-foreground' },
};

const FALLBACK_ICON = { icon: FileText, tint: 'bg-muted text-muted-foreground' };

interface Props {
  activities: CrmActivity[];
  stages: ReadonlyArray<CrmStage>;
  /** Rendered as the closing "Utworzono {date}" line. */
  createdAt?: string | null;
  filter?: TimelineFilter;
  onFilterChange?: (filter: TimelineFilter) => void;
  /** Optional footer link, e.g. to the account's full history. */
  footerLink?: { to: string; label: string };
}

function dayLabel(date: string, now: Date): string {
  const { kind } = relativeDay(date, now);
  if (kind === 'today') return t('crm.deals.timeline.today');
  if (kind === 'yesterday') return t('crm.deals.timeline.yesterday');
  return new Intl.DateTimeFormat(getLanguage() === 'pl' ? 'pl-PL' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
}

function Entry({
  activity,
  stagesByKey,
  lostStageKey,
}: {
  activity: CrmActivity;
  stagesByKey: ReadonlyMap<string, CrmStage>;
  lostStageKey: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const { icon: Icon, tint } = ICONS[activity.type] ?? FALLBACK_ICON;
  const isStageChange = activity.type === 'stage_change';
  const fromKey = isStageChange ? (activity.payload?.fromStageKey as string | undefined) : undefined;
  const toKey = isStageChange ? (activity.payload?.toStageKey as string | undefined) : undefined;
  const toLost = !!toKey && toKey === lostStageKey;
  const longBody =
    (activity.body?.length ?? 0) > LONG_BODY_CHARS || (activity.body?.split('\n').length ?? 0) > LONG_BODY_LINES;

  return (
    <li
      className={cn(
        'grid grid-cols-[1.5rem_1fr] gap-3 py-2',
        toLost && 'border-destructive -ml-3 border-l-2 pl-2',
      )}
    >
      <span className={cn('flex size-6 items-center justify-center rounded-full', tint)}>
        <Icon className="size-3.5" />
      </span>
      <div className="min-w-0 space-y-0.5">
        <div className="text-muted-foreground text-xs">
          {dayjs(activity.occurredAt).format('HH:mm')}
          {activity.authorDisplayName ? ` · ${activity.authorDisplayName}` : ''}
          {' · '}
          {t(`crm.activityType.${activity.type}`)}
        </div>
        {isStageChange ? (
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            {fromKey || toKey ? (
              <>
                <Badge variant="outline">{stagesByKey.get(fromKey ?? '')?.name ?? fromKey ?? '?'}</Badge>
                <span className="text-muted-foreground">→</span>
                <Badge variant={toLost ? 'destructive' : 'outline'}>
                  {stagesByKey.get(toKey ?? '')?.name ?? toKey ?? '?'}
                </Badge>
              </>
            ) : (
              <span className="font-medium">{activity.subject}</span>
            )}
          </div>
        ) : activity.subject ? (
          <div className="text-sm font-medium">{activity.subject}</div>
        ) : null}
        {activity.body ? (
          <div className="text-sm">
            <p className={cn('whitespace-pre-wrap', !expanded && 'line-clamp-4')}>
              {toLost ? (
                <span className="font-medium">{t('crm.deals.timeline.reason')} </span>
              ) : null}
              {activity.body}
            </p>
            {longBody && !expanded ? (
              <button
                type="button"
                className="text-primary text-xs hover:underline"
                onClick={() => setExpanded(true)}
              >
                {t('crm.deals.timeline.more')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Newest-first activity list grouped by day. Shared between the deal sheet
 * and (later) the account page — nothing in here knows what it is attached to.
 */
export function ActivityTimeline({
  activities,
  stages,
  createdAt,
  filter = 'all',
  onFilterChange,
  footerLink,
}: Props) {
  const stagesByKey = useMemo(() => new Map(stages.map((stage) => [stage.key, stage])), [stages]);
  const lostStageKey = useMemo(() => stages.find((stage) => stage.isLost)?.key ?? null, [stages]);

  const filtered = useMemo(() => {
    const list =
      filter === 'entries'
        ? activities.filter((activity) => activity.type !== 'stage_change')
        : filter === 'stageChanges'
          ? activities.filter((activity) => activity.type === 'stage_change')
          : activities;
    return [...list].sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0));
  }, [activities, filter]);

  const groups = useMemo(() => {
    const now = new Date();
    const out: Array<{ key: string; label: string; items: CrmActivity[] }> = [];
    for (const activity of filtered) {
      const key = dayjs(activity.occurredAt).format('YYYY-MM-DD');
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(activity);
      else out.push({ key, label: dayLabel(activity.occurredAt, now), items: [activity] });
    }
    return out;
  }, [filtered]);

  const filters: TimelineFilter[] = ['all', 'entries', 'stageChanges'];

  return (
    <div className="space-y-2">
      {onFilterChange ? (
        <div className="flex gap-1 text-xs">
          {filters.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onFilterChange(key)}
              className={cn(
                'rounded-full px-2.5 py-0.5 transition-colors motion-reduce:transition-none',
                filter === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {t(`crm.deals.timeline.${key}`)}
            </button>
          ))}
        </div>
      ) : null}

      {groups.length === 0 ? (
        <p className="text-muted-foreground py-4 text-sm">{t('crm.deals.timeline.empty')}</p>
      ) : (
        groups.map((group) => (
          <div key={group.key}>
            <div className="bg-background text-muted-foreground sticky top-0 z-10 py-1 text-[11px] font-medium uppercase tracking-wide">
              {group.label}
            </div>
            <ul className="divide-y">
              {group.items.map((activity) => (
                <Entry
                  key={activity.id}
                  activity={activity}
                  stagesByKey={stagesByKey}
                  lostStageKey={lostStageKey}
                />
              ))}
            </ul>
          </div>
        ))
      )}

      {createdAt ? (
        <p className="text-muted-foreground border-t pt-2 text-xs">
          {t('crm.deals.timeline.created', { date: fmtDate(createdAt, true) })}
        </p>
      ) : null}
      {footerLink ? (
        <Link to={footerLink.to} className="text-primary block text-xs hover:underline">
          {footerLink.label}
        </Link>
      ) : null}
    </div>
  );
}
